/**
 * availabilityHtml — `addSendAvailability` 의 self-contained HTML 빌더 (PR-N9a-2 보강).
 *
 * NgwEditor 의 [availability extension](./extensions/availability.ts) 은 ProseMirror atom 노드 +
 * NodeView 로 시간표를 표현하므로 PM `toDOM` 직렬화 결과가 빈 wrapper div 에 그치고, 메일로
 * 발송 시 시간표 본체가 사라진다. 본 모듈은 TinyMCE 의
 * [nine_tinymce_desktop.js L7204~L7361](../../../../apps/owma/public/editor/nine_tinymce_desktop.js#L7204-L7361)
 * 빌더를 순수 함수로 이식하여 `getDataPreprocessed` 가 송신 직전 임시 DOM 의
 * `<div class="nine-availability">` 를 self-contained 마크업으로 교체하는 데 사용한다.
 *
 * 받는 측 메일 클라이언트는 HTML 만으로 시간표 렌더링이 가능하다 (NodeView / JS 불필요).
 *
 * **보안**: `dateTimeTableJson` 은 JSON.parse 후 각 필드를 HTML escape 하여 마크업에 끼워 넣는다.
 * `title` / `tableDesc` / `appDesc` / `appLink` 도 동일 escape.
 */

export interface AvailabilityTimeSlot {
  startDate: string
  startDateLabel: string
  endDate: string
  endDateLabel: string
  timeZone: string
}

export interface AvailabilityDayTable {
  dayLabel: string
  timeSlots: AvailabilityTimeSlot[]
}

export interface AvailabilityHtmlOptions {
  id: string
  dateTimeTableJson: string
  title: string
  tableDesc: string
  appDesc: string
  appLink: string
  /**
   * PR-N9a-2 §15-A — 결과 시작에 `<p><br></p>` (topSpace) 와 끝에 `<!-- End Container -->`
   * (bottomSpace) 를 추가한다. TinyMCE 의
   * [`addSendAvailability` L7322-L7325](../../../../apps/owma/public/editor/nine_tinymce_desktop.js#L7322-L7325)
   * 가 새 insert 시 시간표 위·아래에 wrap 으로 추가하던 빈 줄을 평행으로 재현.
   *
   * **NgwEditor 평행 변경 (2026-05-19)**: TinyMCE 는 `<div class="nine-pg">` 로 단락을 표현하지만
   * NgwEditor 의 PM schema 는 `<p>` 가 paragraph 마크업이다. div 를 wrap 으로 사용하면 PM 의
   * enter handling 시 `TransformError: Inserted content deeper than insertion position` 회귀가
   * 발생한다 — div 가 paragraph 로 흡수되지 않아 split 깊이 mismatch.
   *
   * - `false` (기본): 외곽 `<div class="ms-outlook-mobile-availability-container">` 만 반환.
   * - `true`: 송신 직렬화용. `NgwEditorContent.getDataPreprocessed` 와 NodeView 의 WYSIWYG
   *   시각화가 본 옵션으로 호출하여 작성/송신 화면 모두에서 빈 줄을 표시.
   */
  wrapWithSpace?: boolean
}

/**
 * TinyMCE 의 `toHtmlEntities` ([nine_tinymce_desktop.js:7291~7295]) 와 동일 — a-z, 0-9, 공백 외
 * 모든 문자를 numeric character reference 로 escape. `data-time-tables` attribute 값에 사용.
 */
function toHtmlEntities(str: string): string {
  return str.replace(/./gm, (s) => (/[a-z0-9\s]/i.test(s) ? s : `&#${s.charCodeAt(0)};`))
}

/**
 * 일반 HTML escape — title/tableDesc/appDesc/appLink 의 textContent / attribute 삽입용.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function timeSlotSpace(height: number): string {
  return (
    `<table border="0" cellspacing="0" cellpadding="0"><tbody><tr>` +
    `<td style="line-height: 0; font-size: 0;" height="${height}">&nbsp;</td>` +
    `</tr></tbody></table>`
  )
}

const space6 = timeSlotSpace(6)
const space8 = timeSlotSpace(8)
const space10 = timeSlotSpace(10)

function beginContainer(
  title: string,
  tableDesc: string,
  id: string,
  dateTimeTable: string,
): string {
  return (
    `<div id="${escapeHtml(id)}" class="ms-outlook-mobile-availability-container" ` +
    `contenteditable="false" data-time-tables="${dateTimeTable}" dir="auto" itemscope ` +
    `itemtype="https://schema.org/ChooseAction" ` +
    `style="max-width: 500px; margin: 15px auto; padding: 1px 15px 15px; position: relative; ` +
    `user-select: none; border-radius: 4px;">` +
    `<p class="nine-pg" dir="auto" style="margin: 15px 0;">${escapeHtml(title)}</p>` +
    `<table role="button" border="0" cellspacing="0" cellpadding="0" aria-label="${escapeHtml(tableDesc)}">` +
    `<tbody><tr><td width="100%"><div id="${escapeHtml(id)}-timetables">`
  )
}

function dateHeader(dayLabel: string): string {
  return (
    `<table border="0" width="100%" cellspacing="0" cellpadding="0"><tbody><tr>` +
    `<td width="50%"><table border="0" width="100%" cellspacing="0" cellpadding="0"><tbody><tr>` +
    `<td style="line-height: 0; font-size: 0; background: none; border-top: 1px solid #C7E0F4; ` +
    `height: 1px; width: 100%; margin: 0;" height="1">&nbsp;</td>` +
    `</tr></tbody></table></td>` +
    `<td style="padding: 0 13px; font-weight: 600; color: #0078D7" align="center">` +
    `<span style="white-space: nowrap;">${escapeHtml(dayLabel)}</span></td>` +
    `<td width="50%"><table border="0" width="100%" cellspacing="0" cellpadding="0"><tbody><tr>` +
    `<td style="line-height: 0; font-size: 0; background: none; border-top: 1px solid #C7E0F4; ` +
    `height: 1px; width: 100%; margin: 0;" height="1">&nbsp;</td>` +
    `</tr></tbody></table></td>` +
    `</tr><tr><td colspan="3">${space10}</td></tr></tbody></table>`
  )
}

function timeSlot(data: AvailabilityTimeSlot): string {
  return (
    `<input type="hidden" value="itemtype-event" />` +
    `<form style="margin-bottom: 0px;">` +
    `<table style="background-color: #E9F3FB; border-radius: 4px; font-size: 87.5%; color: #106EBE" ` +
    `border="0" width="100%" cellspacing="0" cellpadding="0"><tbody>` +
    `<tr><td>${space8}</td></tr>` +
    `<tr itemprop="option" itemscope="" itemtype="http://schema.org/Event"><td align="center">` +
    `<time itemprop="startDate" datetime="${escapeHtml(data.startDate)}">${escapeHtml(data.startDateLabel)} </time>-` +
    `<time itemprop="endDate" datetime="${escapeHtml(data.endDate)}">${escapeHtml(data.endDateLabel)} </time> ` +
    `(${escapeHtml(data.timeZone)})</td></tr>` +
    `<tr><td>${space8}</td></tr></tbody></table>` +
    `<input type="hidden" value="${escapeHtml(data.startDate)}" />` +
    `<input type="hidden" value="${escapeHtml(data.endDate)}" /></form>`
  )
}

function timeSlotList(slotList: AvailabilityTimeSlot[]): string {
  let ret = `<table border="0" width="100%" cellspacing="0" cellpadding="0"><tbody>`
  for (const slot of slotList) {
    ret += `<tr><td width="100%">${timeSlot(slot)}</td></tr><tr><td>${space6}</td></tr>`
  }
  ret += `</tbody></table>`
  return ret
}

function endContainer(appDesc: string, appLink: string): string {
  return (
    space6 +
    `</div>` +
    `<table style="font-size: 90%; font-weight: 600;" border="0" width="100%" cellspacing="0" cellpadding="0">` +
    `<tbody><tr><td>${space6}</td></tr><tr>` +
    `<td align="center" valign="middle">` +
    `<a style="color: #0078D7; text-decoration: none;" href="${escapeHtml(appLink)}">${escapeHtml(appDesc)}</a>` +
    `</td></tr></tbody></table>` +
    `</td><td>&nbsp;</td></tr></tbody></table></div>`
  )
}

/**
 * TinyMCE 의 [`window.timeSlot.topSpace` L7214-L7217](../../../../apps/owma/public/editor/nine_tinymce_desktop.js#L7214-L7217)
 * 와 동등. 시간표 위에 빈 줄 한 칸을 추가.
 *
 * **NgwEditor 평행 변경 (2026-05-19)**: TinyMCE 는 `<div class="nine-pg" dir="auto"><br /></div>`
 * 로 단락을 표현하지만, NgwEditor 의 PM schema 는 paragraph 마크업이 `<p>` 이다 (bundle 의
 * `blockFormats: [{ tag: "p", label: "paragraph" }, ...]` 참고). `<div>` 가 NodeView wrapper
 * 안에 직접 들어가면 PM 의 enter handling 시 `TransformError: Inserted content deeper than
 * insertion position` 가 발생 — div 가 paragraph 로 흡수되지 않아 split 깊이 mismatch 가
 * 일어나기 때문. 따라서 NgwEditor 측에서는 `<p><br></p>` 로 평행 마크업을 사용.
 */
const TOP_SPACE_HTML = `<p><br></p>\n<!-- Begin Container -->\n`

/**
 * TinyMCE 의 [`window.timeSlot.bottomSpace` L7275-L7278](../../../../apps/owma/public/editor/nine_tinymce_desktop.js#L7275-L7278)
 * 와 동등. 컨테이너 종료 표시 (시각 효과는 없으나 마크업 의미 유지).
 */
const BOTTOM_SPACE_HTML = `\n<!-- End Container -->`

/**
 * `<div class="nine-availability" data-*>` 를 대체할 self-contained 마크업 생성.
 *
 * `dateTimeTableJson` 이 비어있거나 JSON 파싱 실패 시 null 반환 — caller 는 원본 wrapper 를
 * 그대로 둘지 결정.
 *
 * `opts.wrapWithSpace === true` 면 결과에 TinyMCE 평행의 topSpace/bottomSpace 가 함께 포함된다.
 */
export function buildAvailabilityHtml(opts: AvailabilityHtmlOptions): string | null {
  if (opts.dateTimeTableJson.length === 0) return null

  let dataTables: AvailabilityDayTable[]
  try {
    const parsed = JSON.parse(opts.dateTimeTableJson)
    if (!Array.isArray(parsed)) return null
    dataTables = parsed as AvailabilityDayTable[]
  } catch {
    return null
  }

  const dateTimeTablesStr = toHtmlEntities(JSON.stringify(dataTables))

  let ret = beginContainer(opts.title, opts.tableDesc, opts.id, dateTimeTablesStr)
  for (const dayTable of dataTables) {
    ret += dateHeader(dayTable.dayLabel)
    ret += timeSlotList(dayTable.timeSlots)
  }
  ret += endContainer(opts.appDesc, opts.appLink)

  if (opts.wrapWithSpace === true) {
    return TOP_SPACE_HTML + ret + BOTTOM_SPACE_HTML
  }

  return ret
}
